import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AuthContext } from '@/lib/auth/models/auth.model';
import { 
  DashboardItinerary, 
  DashboardTravelRequest,
  type IDashboardViewData,
  type IDashboardTravelRequestData
} from '@/lib/models/dashboard';

// Re-export types for backward compatibility
export { DashboardItinerary, DashboardTravelRequest };

interface UseDashboardDataOptions {
  itineraryStatusFilter?: string;
  travelRequestStatusExclude?: string;
  disableAutoFetch?: boolean;
}

/**
 * Hook unificato per dashboard: fa una singola chiamata RPC e restituisce
 * sia gli itinerari che tutte le travel requests
 */
export const useDashboardData = (options: UseDashboardDataOptions = {}) => {
  const [itineraries, setItineraries] = useState<DashboardItinerary[]>([]);
  const [travelRequests, setTravelRequests] = useState<DashboardTravelRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useDashboardData deve essere usato all\'interno di un AuthProvider');
  }

  /**
   * Fetch unificato: due chiamate RPC parallele (RETURNS TABLE, no jsonb_agg in Postgres)
   */
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [itinerariesResult, travelRequestsResult] = await Promise.all([
        supabase.rpc('get_dashboard_itineraries', {
          p_itinerary_status: optionsRef.current.itineraryStatusFilter || null,
        }),
        supabase.rpc('get_dashboard_travel_requests', {
          p_status_exclude: optionsRef.current.travelRequestStatusExclude || null,
        }),
      ]);

      if (itinerariesResult.error || travelRequestsResult.error) {
        setError(String(t('errors.loadFailed')));
        toast({
          title: String(t('common.error')),
          description: String(t('errors.loadFailed')),
          variant: "destructive",
        });
        return;
      }

      // 1. Mappa itinerari
      const mappedItineraries = ((itinerariesResult.data as IDashboardViewData[]) || []).map(
        (item: IDashboardViewData) => new DashboardItinerary(item)
      );

      // 2. Mappa travel requests
      const mappedTravelRequests = ((travelRequestsResult.data as IDashboardTravelRequestData[]) || []).map(
        (req: IDashboardTravelRequestData) => new DashboardTravelRequest(req)
      );

      setItineraries(mappedItineraries);
      setTravelRequests(mappedTravelRequests);
    } catch (err) {
      setError(String(t('errors.generic')));
      toast({
        title: String(t('common.error')),
        description: String(t('errors.generic')),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Auto-fetch al mount
  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vuoto: esegue solo al mount

  // Funzione per refresh manuale
  const refreshData = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  // Accetta una travel request assegnata
  const acceptTravelRequest = useCallback(async (requestId: string) => {
    try {
      const { user } = authContext;
      const { error: updateError } = await supabase
        .from('travel_requests')
        .update({
          status: 'assigned',
          updated_at: new Date().toISOString(),
          assigned_designer_id: user.id
        })
        .eq('id', requestId);

      if (updateError) {
        toast({
          title: String(t('common.error')),
          description: String(t('errors.generic')),
          variant: "destructive",
        });
        return false;
      }

      await refreshData();

      toast({
        title: String(t('common.success')),
        description: 'Request accepted successfully',
      });

      return true;
    } catch (err) {
      toast({
        title: String(t('common.error')),
        description: String(t('errors.generic')),
        variant: "destructive",
      });
      return false;
    }
  }, [authContext, refreshData, t]);

  // Rifiuta una travel request assegnata
  const declineTravelRequest = useCallback(async (requestId: string) => {
    try {
      const { user } = authContext;
      const { error: updateError } = await supabase
        .from('travel_requests')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('assigned_designer_id', user.id);

      if (updateError) {
        toast({
          title: String(t('common.error')),
          description: String(t('errors.generic')),
          variant: "destructive",
        });
        return false;
      }

      await refreshData();

      toast({
        title: String(t('common.success')),
        description: 'Request declined successfully',
      });

      return true;
    } catch (err) {
      toast({
        title: String(t('common.error')),
        description: String(t('errors.generic')),
        variant: "destructive",
      });
      return false;
    }
  }, [authContext, refreshData, t]);

  return {
    itineraries,
    travelRequests,
    loading,
    error,
    refreshData,
    acceptTravelRequest,
    declineTravelRequest,
  };
};
